import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SecurityContext,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { BlogService } from '@services/http/blog.service';

import {
  CommonModule,
  DatePipe,
  isPlatformBrowser,
  NgOptimizedImage,
} from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
// import { BlogCtaDialogComponent } from '@dialogs/genericDialogs/blogCtaDialog/blogCtaDialog';
import { Blog } from '@models/blog.model';
import { LocalizationService } from '@services/logic/localization.service';
import { articlePath } from './article-path';

import { setBreadCrumb } from '@utils/setBreadCrumb';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import { FooterComponent } from '@components/shared/footer/footer.component';

import { ScrollProgressBarDirective } from '@app/directives/scroll-progress-bar-directive';
// import { ScrollToTopDirective } from '@app/directives/scroll-to-top-directive';

import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';

import { CARD_HEIGHT, CARD_WIDTH } from '@app/pages/blog/blog.component';
import { SafePipe } from '@app/pipes/safe';
import { SEOService } from '@services/logic/seo.service';
import { TranslateService } from '@services/logic/translate.service';
import { ServerSideResponseService } from '@services/logic/serverSideResponse.service';
import { BlogPostingSchema } from '@app/pages/blog/details/schema/schema.helper';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  providers: [SafePipe, JsonLdPipe, TranslatePipe, SEOService],
  selector: 'app-blog-detail',
  templateUrl: './blog.detail.component.html',
  styleUrls: ['./blog.detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    DatePipe,
    BreadcrumbListComponent,
    ScrollProgressBarDirective,
    //ScrollToTopDirective,
    MatIconModule,
    TranslatePipe,
    RouterLink,
    NgOptimizedImage,
    FooterComponent,
    JsonLdPipe,
    SafePipe,
  ],
})
export class BlogDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly localization = inject(LocalizationService);

  private isBrowser: boolean = true;
  private destroy$: Subject<void> = new Subject<void>();
  private h2ProcessingTimeoutId: number | null = null;
  public guideSlug?: string;
  otherBlogs: Blog[] = [];
  partsOfUrl!: { name: string; url: string }[];
  currentBlogCategory?: string;
  defaultImage = '/assets/svg/logo.svg';
  // URL Params
  @Input() private slug?: string;
  private headCategory?: string;
  private subCategory?: string;
  public innerWidth: number = 0;
  public currentBlog!: Blog;
  h2Texts: { id: string; text: string }[] = [];
  tableOfContentExpanded = false;
  breadCrumbSchema: any;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private blogService: BlogService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private seoService: SEOService,
    private router: Router,
    private serverSideResponseService: ServerSideResponseService,
    private translate: TranslateService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.subscribeToParams();
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.innerWidth = window.innerWidth;
      this.processH2Elements();
    }
  }

  ngOnDestroy() {
    // Clear any pending timeouts to prevent memory leaks
    if (this.h2ProcessingTimeoutId !== null) {
      clearTimeout(this.h2ProcessingTimeoutId);
      this.h2ProcessingTimeoutId = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  subscribeToParams() {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.guideSlug = this.route.snapshot.paramMap.get('guideSlug')!;
      this.headCategory =
        this.route.snapshot.paramMap.get('headCategory') ?? '';
      this.subCategory = this.route.snapshot.paramMap.get('subCategory') ?? '';
      this.slug = this.route.snapshot.paramMap.get('slug') ?? '';
      if (this.guideSlug || this.slug) {
        this.getBlogDetailBySlug(this.guideSlug || this.slug);
      }
    });
  }

  private getBlogDetailBySlug(slug: string) {
    this.blogService
      .fetchBlogById(slug, this.headCategory, this.subCategory)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.currentBlog = new Blog(res);

          // Sanitize and process content
          const hasRichTextProperty =
            typeof this.currentBlog?.content === 'object' &&
            'rich_text' in this.currentBlog.content;

          const htmlString = hasRichTextProperty
            ? this.currentBlog.content.rich_text
            : this.currentBlog.content;

          const sanitizedContent = this.sanitizer.sanitize(
            SecurityContext.HTML,
            htmlString.replace(/\\n/g, '\n').replace(/\\r/g, ''),
          );

          if (hasRichTextProperty) {
            this.currentBlog.content.rich_text = sanitizedContent;
          } else {
            this.currentBlog.content = sanitizedContent;
          }

          this.setMeta();
          this.getOtherBlogs();
          this.cdr.markForCheck();
        },
        (error) => {
          // 301 Permanent redirects handle correct by response status code!
          if (error.status === 200 && error.url && this.isBrowser) {
            // Refresh the component with the new URL
            window.location.href = error.url;
          }
          if (error.status === 404) {
            // Set 404 status code for server response during SSR
            this.serverSideResponseService.setNotFound();
            return this.router.navigate(['/404'], {
              skipLocationChange: true,
            });
          }
          return;
        },
      );
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.innerWidth = window.innerWidth;
      if (this.innerWidth > 990) {
        this.tableOfContentExpanded = true;
      }
    }
  }

  expandTableOfContent() {
    this.tableOfContentExpanded = !this.tableOfContentExpanded;
  }

  scrollTo(event: MouseEvent, elementId: string): void {
    if (!this.isBrowser) return;

    event.preventDefault();
    const element = this.document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  setBreadCrumb() {
    this.partsOfUrl = setBreadCrumb({
      entity: this.currentBlog,
      hubIndex: this.seg.suffix.hubIndex,
      url: this.router.url,
    });
  }

  setMeta() {
    this.setBreadCrumb();
    this.seoService.setPageMeta({
      metaTitle: this.currentBlog.title_tag!,
      metaDesc: this.currentBlog.meta_description!,
      img: this.currentBlog.imgUrl,
      // Not `router.url`: the article is routable under several category prefixes.
      canonicalUrl: `${this.env.domain}${this.generateLink(this.currentBlog)}`,
    });
    this.injectJSONLDSchema();
    this.injectBreadCrumbSchema();
    // this.openCouponDiscountDialog();
  }

  generateLink(blog: Blog): string {
    return articlePath(blog, this.seg, this.localization.seoRoute('blog'));
  }

  openCouponDiscountDialog() {
    /*   this.dialog.open(BlogCtaDialogComponent, {
      hasBackdrop: false,
      position: { bottom: '0' },
      width: '100vw',
      maxWidth: '100vw',
      data: {
        title: 'Skal du bruge hjælp til en opgave?',
      },
    });*/
  }

  getOtherBlogs() {
    this.blogService
      .fetchRelatedPosts({
        currentBlogId: this.currentBlog.id!,
        asCat: this.currentBlog?.asCat!,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.otherBlogs = res as Blog[];
        this.cdr.markForCheck();
      });
  }

  onBackBtnPress() {
    const url = this.headCategory
      ? `/${this.headCategory}/${this.seg.suffix.hubIndex}`
      : '/blog';
    this.router.navigate([url]);
  }

  injectJSONLDSchema() {
    const raw =
      this.currentBlog?.content?.rich_text ?? this.currentBlog.content;
    const desc = typeof raw === 'string' ? raw.replace(/<\/?p[^>]*>/g, '') : '';

    const altTxtRaw = this.currentBlog?.excerpt;
    const altTxt =
      typeof altTxtRaw === 'string'
        ? altTxtRaw.replace(/<\/?p[^>]*>/g, '')
        : '';
    const schema = this.translate.data?.SEO?.SCHEMA;
    this.seoService.setArticleScheme(
      BlogPostingSchema(
        this.env,
        this.seg,
        this.currentBlog,
        altTxt,
        desc,
        this.currentBlogCategory!,
        this.headCategory!,
        {
          authorName: schema?.DEFAULT_ARTICLE_AUTHOR ?? this.env.appName,
        },
      ),
    );
  }

  injectBreadCrumbSchema() {
    // Built from the same parts as the visible breadcrumb (set in
    // setBreadCrumb) so the schema always mirrors routable URLs
    if (!this.partsOfUrl?.length) {
      this.setBreadCrumb();
    }
    this.breadCrumbSchema = this.seoService.buildBreadCrumbSchema(
      this.partsOfUrl ?? [],
    );
  }

  private processH2Elements(): void {
    if (!this.isBrowser) return;

    // Wait for content to be rendered
    // Store the timeout ID so we can clear it if the component is destroyed
    this.h2ProcessingTimeoutId = window.setTimeout(() => {
      const contentElement = this.document.getElementById('content-text');
      if (contentElement) {
        const h2Elements = contentElement.getElementsByTagName('h2');
        this.h2Texts = Array.from(h2Elements).map((h2, index) => {
          const id = `h2-${index}`;
          h2.id = id;
          return {
            id,
            text: h2.textContent || '',
          };
        });
      }
      this.h2ProcessingTimeoutId = null;
      this.cdr.markForCheck();
    }, 0);
  }

  getFaqText(text: string | undefined): string {
    return text || '';
  }

  protected readonly environment = this.env;
  protected readonly CARD_HEIGHT = CARD_HEIGHT;
  protected readonly CARD_WIDTH = CARD_WIDTH;
}
